import React from 'react';
import * as LC from './lazyComponents';
import { EUR_MARKET_COUNTRY_CODES } from '../utils/geoCurrency';
import { REVOLUT_PAY_COUNTRY } from '../../paymentMethods/revolutPay/isAvailable';
import { type MainLayoutCustomStylesWithStatesProps } from '@applyft-web/ui-components';

export interface CommonProps {
  readonly withButton?: boolean;
  readonly withoutWrap?: boolean;
  readonly wrapperProps?: {
    adaptive?: boolean;
    customStyles?: MainLayoutCustomStylesWithStatesProps;
  };
}

export interface ConfigProps extends CommonProps {
  readonly component: LC.LazyComponentWithPreload<React.ComponentType<any>>;
  readonly images?: string[];
  readonly componentProps?: object;
}

const checkoutCommonProps = {
  images: [
    '/assets/images/card-brands/amex.png',
    '/assets/images/card-brands/discover.png',
    '/assets/images/card-brands/mastercard.png',
    '/assets/images/card-brands/visa.png',
    '/assets/images/card-brands/maestro.png',
    '/assets/images/card-brands/apple.png',
    '/assets/images/card-brands/google.png',
    '/assets/images/card-brands/paypal.png',
    '/assets/images/wallets/applePay.png',
    '/assets/images/wallets/googlePay.png',
    '/assets/images/wallets/paypal.png',
    '/assets/images/wallets/applePay-small.png',
    '/assets/images/wallets/googlePay-small.png',
    '/assets/images/wallets/paypal-small.png',
    '/assets/images/wallets/revolut.png',
    '/assets/images/revolut-pay.png',
    '/assets/images/wallets/pix.png',
    '/assets/images/logo-pix.png',
    '/assets/images/wallets/blik.png',
  ],
  withButton: false,
  wrapperProps: {
    adaptive: true,
    customStyles: { default: 'padding-top:0;padding-bottom:0;' },
  },
};

const welcomeCommonProps = {
  withButton: false,
  wrapperProps: {
    adaptive: true,
    customStyles: { default: 'padding-top:0;' },
  },
};

export const pagesConfig: { [key: string]: ConfigProps } = {
  one_pager: {
    ...welcomeCommonProps,
    component: LC.Lp1Welcome,
  },
  start_cht_onb: {
    withoutWrap: true,
    withButton: false,
    component: LC.StartChtOnb,
    componentProps: {
      screenId: 'start_cht_onb',
    },
  },
  prc_lct_cht_onb: {
    withoutWrap: true,
    withButton: false,
    component: LC.PrcLctChtOnb,
    componentProps: {
      screenId: 'prc_lct_cht_onb',
    },
  },
  zn_alrt_ctn_onb: {
    withoutWrap: true,
    withButton: false,
    component: LC.ZnAlrtCtnOnb,
    componentProps: {
      screenId: 'zn_alrt_ctn_onb',
    },
  },
  lc_hs_ctn_onb: {
    withoutWrap: true,
    withButton: false,
    component: LC.LcHsCtnOnb,
    componentProps: {
      screenId: 'lc_hs_ctn_onb',
    },
  },
  one_pager_ctn_onb: {
    withoutWrap: true,
    withButton: false,
    component: LC.OnePagerCtnOnb,
    componentProps: {
      screenId: 'one_pager_ctn_onb',
    },
  },
  one_pager_all: {
    ...welcomeCommonProps,
    component: LC.ComboWelcome,
  },
  one_pager_all_ws_risk: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllWs,
    images: ['/assets/images/welcome/welcome-combo-woman-safety.webp'],
    componentProps: {
      screenId: 'one_pager_all_ws_risk',
      titleKey: 'women_safety_risk_title',
    },
  },
  one_pager_all_ws_safe: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllWs,
    images: ['/assets/images/welcome/welcome-combo-woman-safety.webp'],
    componentProps: {
      screenId: 'one_pager_all_ws_safe',
      titleKey: 'women_safety_safe_title',
    },
  },
  one_pager_all_ws_locate: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllWs,
    images: ['/assets/images/welcome/welcome-combo-woman-safety.webp'],
    componentProps: {
      screenId: 'one_pager_all_ws_locate',
      titleKey: 'women_safety_locate_title',
    },
  },
  one_pager_all_gn: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllGn,
    componentProps: {
      screenId: 'one_pager_all_gn',
    },
  },
  one_pager_all_gn_lng: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllGn,
    componentProps: {
      screenId: 'one_pager_all_gn_lng',
      withLanguagePicker: true,
    },
  },
  one_pager_kwtt: {
    ...welcomeCommonProps,
    component: LC.OnePagerKwtt,
    componentProps: {
      screenId: 'one_pager_kwtt',
    },
  },
  one_pager_all_gnhdl: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllGnhdl,
    componentProps: {
      screenId: 'one_pager_all_gnhdl',
    },
  },
  one_pager_all_us: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllUs,
    componentProps: {
      screenId: 'one_pager_all_us',
    },
  },
  one_pager_all_dc: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllDc,
    componentProps: {
      screenId: 'one_pager_all_dc',
    },
  },
  one_pager_cplo: {
    ...welcomeCommonProps,
    component: LC.OnePagerAllDc,
    componentProps: {
      screenId: 'one_pager_cplo',
      titleKey: 'stay_safe_wherever_you_go',
      descriptionKey: 'simply_enter_a_phone_number',
    },
  },
  one_pager_lf: {
    ...welcomeCommonProps,
    component: LC.OnePagerLf,
    wrapperProps: {
      adaptive: true,
      customStyles: { default: 'padding-top:0;padding-bottom:0;' },
    },
    componentProps: {
      screenId: 'one_pager_lf',
    },
  },
  one_pager_all2: {
    ...welcomeCommonProps,
    component: LC.ComboWelcome,
    componentProps: {
      screenId: 'one_pager_all2',
      reversed: true,
      customImg: true,
    },
  },
  one_pager_ai: {
    ...welcomeCommonProps,
    component: LC.ComboWelcome,
    componentProps: {
      screenId: 'one_pager_ai',
      withAILabel: true,
    },
  },
  one_pager_l1: {
    ...welcomeCommonProps,
    component: LC.ComboWelcome,
    componentProps: {
      screenId: 'one_pager_l1',
      howWorksUp: true,
      legacyOnePager: true,
    },
  },
  one_pager_l2: {
    ...welcomeCommonProps,
    component: LC.ComboWelcome,
    componentProps: {
      screenId: 'one_pager_l2',
      threeLabels: true,
      legacyOnePager: true,
    },
  },
  searching: {
    component: LC.Map,
    withButton: false,
    withoutWrap: true,
  },
  searching_2: {
    component: LC.ComboMap,
    withButton: false,
    withoutWrap: true,
  },
  searching_ai: {
    component: LC.ComboMap,
    withButton: false,
    withoutWrap: true,
    componentProps: {
      withAILabel: true,
    },
  },
  searching_ctn_onb: {
    component: LC.ComboMap,
    withButton: false,
    withoutWrap: true,
    componentProps: {
      screenId: 'searching_ctn_onb',
    },
  },
  signup: {
    component: LC.Signup,
    withButton: false,
    componentProps: { screenId: 'signup' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 0;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  email_consent: {
    component: LC.EmailConsent,
    images: ['/assets/images/email-consent/icon.png'],
    componentProps: {
      screenId: 'email_consent',
    },
    withoutWrap: true,
  },
  features: {
    component: LC.ComboFeatures,
    withButton: false,
    withoutWrap: true,
    componentProps: {
      screenId: 'features',
      version: 'default',
    },
  },
  features_2: {
    component: LC.ComboFeatures,
    withButton: false,
    withoutWrap: true,
    componentProps: {
      screenId: 'features_2',
      version: 'v2',
    },
  },
  signup_testim: {
    component: LC.ComboSignup,
    withButton: false,
    componentProps: { screenId: 'signup_testim' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 0; display: block;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  signup_ctn_onb: {
    component: LC.SignupCtnOnb,
    withButton: false,
    componentProps: { screenId: 'signup_ctn_onb' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 0; display: block;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  signup_lf: {
    component: LC.SignupLf,
    withButton: false,
    componentProps: { screenId: 'signup_lf' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 0; display: block;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  checkout_v3_tr: {
    ...checkoutCommonProps,
    component: LC.Checkout,
    componentProps: {
      screenId: 'checkout_v3_tr',
      paywallType: 'combo',
    },
  },
  checkout_h3_pp: {
    ...checkoutCommonProps,
    component: LC.Checkout,
    componentProps: {
      screenId: 'checkout_h3_pp',
      paywallType: 'combo',
      checkoutType: 'horizontal',
    },
  },
  checkout_h3_pp_braintree: {
    ...checkoutCommonProps,
    component: LC.Checkout,
    componentProps: {
      screenId: 'checkout_h3_pp_braintree',
      paywallType: 'combo',
      checkoutType: 'horizontal',
      forceProvider: 'braintree',
    },
  },
  checkout_v3_pp: {
    ...checkoutCommonProps,
    component: LC.Checkout,
    componentProps: {
      screenId: 'checkout_v3_pp',
      paywallType: 'combo',
      checkoutType: 'vertical',
    },
  },
  paycheck1_pr5_h_win: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck1_pr5_h_win',
      paywallType: 'combo',
    },
  },
  paycheck1_pr5_h_ai: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck1_pr5_h_ai',
      paywallType: 'combo',
      withAILabel: true,
    },
  },
  paycheck2_pr3_h_dsc1: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_dsc1',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      withDiscountLabel: true,
    },
  },
  paycheck2_pr3_h_dsc2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_dsc2',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      withBanner: true,
    },
  },
  paycheck2_pr3_h_dsc3: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_dsc3',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      withDiscountLabelV2: true,
    },
  },
  paycheck2_pr3_h_dsc4: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_dsc4',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      withDiscountLabelV2: true,
      withBannerV2: true,
    },
  },
  paycheck2_pr3_h_scp1: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_scp1',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      socialProof: 'v1',
    },
  },
  paycheck2_pr3_h_scp2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_scp2',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      socialProof: 'v2',
    },
  },
  paycheck2_pr3_h_scp3: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_scp3',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      socialProof: 'v3',
    },
  },
  paycheck2_pr3_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.Paycheck2pr3hFastpay2,
    componentProps: {
      screenId: 'paycheck2_pr3_h_fastpay2',
    },
  },
  paycheck2_pr3_h_fastpay2_tp: {
    ...checkoutCommonProps,
    component: LC.Paycheck2pr3hFastpay2Tp,
    componentProps: {
      screenId: 'paycheck2_pr3_h_fastpay2_tp',
    },
  },
  paycheck2_pr3_h_amz: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_amz',
      paywallType: 'combo_3days_gbp',
      isApplePayFirst: true,
      withAmazonPay: true,
      amazonPayCountryCodes: [...EUR_MARKET_COUNTRY_CODES, 'GB'],
      preferAmazonPayTab: true,
    },
  },
  paycheck2_pr3_h_fastpay_bm: {
    ...checkoutCommonProps,
    component: LC.Paycheck2pr3hFastpayBm,
    componentProps: { screenId: 'paycheck2_pr3_h_fastpay_bm' },
  },
  paycheck2_pr3_h_amz_rvl: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_amz_rvl',
      paywallType: 'combo_3days_gbp',
      isApplePayFirst: true,
      withAmazonPay: true,
      amazonPayCountryCodes: [...EUR_MARKET_COUNTRY_CODES, 'GB'],
      preferAmazonPayTab: true,
      withRevolutPay: true,
      revolutPayCountryCodes: EUR_MARKET_COUNTRY_CODES,
      isVerticalLayout: true,
    },
  },
  checkout_ctn_onb: {
    ...checkoutCommonProps,
    component: LC.CheckoutCtnOnb,
    componentProps: {
      screenId: 'checkout_ctn_onb',
    },
  },
  paycheck_parental_lg2: {
    ...checkoutCommonProps,
    component: LC.PaycheckParentalLg2,
    componentProps: {
      screenId: 'paycheck_parental_lg2',
    },
  },
  paycheck2_pr3_h_pln: {
    ...checkoutCommonProps,
    component: LC.Paycheck2pr3hPln,
    componentProps: {
      screenId: 'paycheck2_pr3_h_pln',
    },
  },
  paycheck2_pr3_h_upi: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_upi',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      isShowUpi: true,
      isUpiFirst: true,
    },
  },
  paycheck2_pr3_h_strp: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_strp',
      paywallType: 'combo_3days',
      useStripeOcs: true,
    },
  },
  paycheck2_pr3_h_strp2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_strp2',
      paywallType: 'combo_3days',
      useStripeOcs: true,
      useOcsFxQuotes: true,
    },
  },
  paycheck2_pr1w_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr1w_h_fastpay2',
      paywallType: 'combo_1week_legal',
      isApplePayFirst: true,
      isLegalPaywall: true,
    },
  },
  paycheck2_pr1w_h_am_us: {
    ...checkoutCommonProps,
    component: LC.Paycheck2pr1wHAmUs,
    componentProps: { screenId: 'paycheck2_pr1w_h_am_us', amazonPayCountryCodes: ['US'] },
  },
  paycheck2_pr1w_h_fastpay2_cap: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr1w_h_fastpay2_cap',
      paywallType: 'combo_1week_legal',
      isApplePayFirst: true,
      isLegalPaywall: true,
      isShowCashApp: true,
      withPaypal: false,
    },
  },
  paycheck2_pr3_h_only: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3_h_only',
      paywallType: 'trial_3_days',
      isApplePayFirst: true,
    },
  },
  test_pipeline_checkout: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'test_pipeline_checkout',
      paywallType: 'test_pipeline',
      isApplePayFirst: true,
    },
  },
  paycheck3_pr3d_h_lvl: {
    ...checkoutCommonProps,
    component: LC.Checkout,
    componentProps: {
      screenId: 'paycheck3_pr3d_h_lvl',
      checkoutType: 'horizontal',
      paywallType: 'lvl',
    },
  },
  paycheck4_pr1_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck4_pr1_h_fastpay2',
      paywallType: 'combo_1day',
      isApplePayFirst: true,
    },
  },
  paycheck5_pr1_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck5_pr1_h_fastpay2',
      paywallType: 'combo_1day_less',
      isApplePayFirst: true,
    },
  },
  paycheck2_pr3d_h_rvl: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck2_pr3d_h_rvl',
      paywallType: 'combo_3days',
      withRevolutPay: true,
      revolutPayCountryCodes: [REVOLUT_PAY_COUNTRY],
      preferRevolutPayTab: true,
    },
  },
  paycheck4_pr3_h_brp: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'paycheck4_pr3_h_brp',
      paywallType: 'combo_3days_brl',
      isPixFirst: true,
      isShowPixAutomatico: true,
    },
  },
  crm_pr3_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'crm_pr3_h_fastpay2',
      paywallType: 'combo_3days',
      isApplePayFirst: true,
      useAuth: true,
    },
  },
  crm60_pr3_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'crm60_pr3_h_fastpay2',
      paywallType: 'combo_3days_crm',
      isApplePayFirst: true,
      useAuth: true,
    },
  },
  crm75_pr3_h_fastpay2: {
    ...checkoutCommonProps,
    component: LC.ComboCheckout,
    componentProps: {
      screenId: 'crm75_pr3_h_fastpay2',
      paywallType: 'combo_3days_crm_less',
      isApplePayFirst: true,
      useAuth: true,
    },
  },
  upsell_an_private: {
    component: LC.ComboUpsell,
    images: [
      '/assets/images/upsell/anonymous_mode.png',
      '/assets/images/upsell/private_chat.png',
      '/assets/images/upsell/discount-label.svg',
    ],
    withButton: false,
    componentProps: { screenId: 'upsell_an_private' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 20px;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  upsell_an_private_ctn_onb: {
    component: LC.ComboUpsell,
    images: [
      '/assets/images/upsell/anonymous_mode.png',
      '/assets/images/upsell/private_chat.png',
      '/assets/images/upsell/discount-label.svg',
    ],
    withButton: false,
    componentProps: {
      screenId: 'upsell_an_private_ctn_onb',
    },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 20px;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  upsell_an_private_tp: {
    component: LC.ComboUpsellTp,
    images: [
      '/assets/images/upsell/anonymous_mode.png',
      '/assets/images/upsell/private_chat.png',
      '/assets/images/upsell/discount-label.svg',
    ],
    withButton: false,
    componentProps: { screenId: 'upsell_an_private_tp' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 20px;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  upsell_trc_v1: {
    images: [
      '/assets/images/flows/upsell_trace/upsell_trc_v1/unknown-number.webp',
      '/assets/images/flows/upsell_trace/upsell_trc_v1/data-leak.webp',
      '/assets/images/flows/upsell_trace/upsell_trc_v1/check-seller.webp',
      '/assets/images/flows/upsell_trace/upsell_trc_v1/verify-someone.webp',
    ],
    component: LC.UpsellTrcV1,
    withoutWrap: true,
  },
  upsell_trc_v2: {
    images: ['/assets/images/flows/upsell_trace/upsell_trc_v2/trace.webp'],
    component: LC.UpsellTrcV2,
    withoutWrap: true,
  },
  test_pipeline_upsell_subscription: {
    component: LC.ComboUpsell,
    images: [
      '/assets/images/upsell/anonymous_mode.png',
      '/assets/images/upsell/private_chat.png',
      '/assets/images/upsell/discount-label.svg',
    ],
    withButton: false,
    componentProps: { screenId: 'test_pipeline_upsell_subscription' },
    wrapperProps: {
      adaptive: true,
      customStyles: {
        default: 'padding-top: 20px;',
        tablet: 'justify-content: flex-start;',
      },
    },
  },
  success: {
    component: LC.Success,
    withButton: false,
    withoutWrap: true,
    componentProps: { screenId: 'success' },
  },
  success_ctn_onb: {
    component: LC.Success,
    withButton: false,
    withoutWrap: true,
    componentProps: { screenId: 'success_ctn_onb' },
  },
  upsell_guide: {
    component: LC.UpsellGuide,
    images: [
      '/assets/images/upsell-guide/safety-toolkit.png',
      '/assets/images/upsell-guide/success-email.png',
      '/assets/images/upsell-guide/success-bg.png',
    ],
    withButton: false,
    withoutWrap: false,
    componentProps: { screenId: 'upsell_guide' },
    wrapperProps: {
      customStyles: {
        default: 'max-width: 460px;',
      },
    },
  },
  upsell_guide_ctn_onb: {
    component: LC.UpsellGuide,
    images: [
      '/assets/images/upsell-guide/safety-toolkit.png',
      '/assets/images/upsell-guide/success-email.png',
      '/assets/images/upsell-guide/success-bg.png',
    ],
    withButton: false,
    withoutWrap: false,
    componentProps: { screenId: 'upsell_guide_ctn_onb' },
    wrapperProps: {
      customStyles: {
        default: 'max-width: 460px;',
      },
    },
  },
  upsell_guide_tp: {
    component: LC.UpsellGuideTp,
    images: [
      '/assets/images/upsell-guide/safety-toolkit.png',
      '/assets/images/upsell-guide/success-email.png',
      '/assets/images/upsell-guide/success-bg.png',
    ],
    withButton: false,
    withoutWrap: false,
    componentProps: { screenId: 'upsell_guide_tp' },
    wrapperProps: {
      customStyles: {
        default: 'max-width: 460px;',
      },
    },
  },
  test_pipeline_upsell_one_time: {
    component: LC.UpsellGuide,
    images: ['/assets/images/upsell-guide/safety-toolkit.png'],
    withButton: false,
    withoutWrap: false,
    componentProps: { screenId: 'test_pipeline_upsell_one_time' },
  },
  'auto-signup': {
    component: LC.AutoSignup,
    withButton: false,
    withoutWrap: true,
  },
  'checkout-test': {
    ...checkoutCommonProps,
    component: LC.LegalCheckout,
  },
  '*': {
    component: LC.FlowEntry,
    withoutWrap: true,
    withButton: false,
  },
};

export interface RoutesProps extends CommonProps {
  readonly path: string;
  readonly element: React.JSX.Element;
}

export const pagesRoutes: RoutesProps[] = Object.entries(pagesConfig).map(([path, conf]) => {
  const { component: Comp, componentProps, withButton = true } = conf;

  return {
    ...conf,
    path: `/${path}`,
    element: <Comp {...componentProps} />,
    withButton,
  };
});
